import { useState, useRef } from "react";
import { X, UploadCloud, Loader2, Users, AlertCircle, CheckCircle2, Plus, Trash2 } from "lucide-react";
import axios from "axios";

/**
 * Visitor ID Importer — modal for uploading a Website Visitor ID PDF
 * (CallCenter / third-party ID vendor format) and persisting the
 * extracted aggregate metrics into `visitor_id_reports`.
 *
 * Extraction runs in two stages on the backend: PyPDF2 text parsing for
 * KPIs + industries + top pages, plus GPT-5.4 Vision on the donut-chart
 * pages for age / net worth / home-value percentages. The user reviews
 * everything in a single editable confirmation form before save.
 *
 * Individual visitor PII is NEVER persisted (per user preference — only
 * aggregate counts + breakdowns are stored).
 */

const numInput = (value, onChange, testId, step = "1") => (
  <input
    type="number"
    min="0"
    step={step}
    value={value ?? 0}
    onChange={(e) => onChange(Number(e.target.value || 0))}
    className="w-full px-2 py-1 text-sm border border-slate-300 rounded-md focus:border-violet-500 focus:ring-1 focus:ring-violet-500 tabular-nums"
    data-testid={testId}
  />
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 block mb-0.5">{label}</label>
    {children}
  </div>
);

const blankState = () => ({
  addresses_captured: 0,
  verified_emails: 0,
  businesses_captured: 0,
  age_range: [],
  net_worth: [],
  home_value: [],
  top_industries: [],
  top_pages: [],
});

// Editable list of {rangeKey, pct} — used for age / net_worth / home_value.
function BreakdownEditor({ title, rangeKey = "range", items, onChange, testIdPrefix }) {
  const update = (i, field, value) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: field === "pct" ? Number(value || 0) : value };
    onChange(next);
  };
  const remove = (i) => {
    const next = [...items];
    next.splice(i, 1);
    onChange(next);
  };
  const add = () => onChange([...items, { [rangeKey]: "", pct: 0 }]);

  return (
    <div className="bg-white rounded-xl border-2 border-violet-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-bold text-slate-900">{title}</p>
        <button
          onClick={add}
          className="text-xs text-violet-600 hover:text-violet-700 font-semibold inline-flex items-center gap-1"
          data-testid={`${testIdPrefix}-add`}
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      {items.length === 0 && (
        <p className="text-xs text-slate-400 italic">Nothing extracted. Add rows manually if you have values.</p>
      )}
      <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Label (e.g. 60-69)"
              value={it[rangeKey] || ""}
              onChange={(e) => update(i, rangeKey, e.target.value)}
              className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded-md"
              data-testid={`${testIdPrefix}-label-${i}`}
            />
            <div className="relative w-24">
              <input
                type="number"
                min="0"
                step="0.01"
                value={it.pct ?? 0}
                onChange={(e) => update(i, "pct", e.target.value)}
                className="w-full pl-2 pr-6 py-1 text-sm border border-slate-300 rounded-md tabular-nums"
                data-testid={`${testIdPrefix}-pct-${i}`}
              />
              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
            </div>
            <button
              onClick={() => remove(i)}
              className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500"
              data-testid={`${testIdPrefix}-remove-${i}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// Editable list of {industry, count} or {url, count}
function CountList({ title, labelKey, items, onChange, placeholder, testIdPrefix }) {
  const update = (i, field, value) => {
    const next = [...items];
    next[i] = { ...next[i], [field]: field === "count" ? Number(value || 0) : value };
    onChange(next);
  };
  const remove = (i) => {
    const next = [...items];
    next.splice(i, 1);
    onChange(next);
  };
  const add = () => onChange([...items, { [labelKey]: "", count: 0 }]);
  return (
    <div className="bg-white rounded-xl border-2 border-violet-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-bold text-slate-900">{title}</p>
        <button
          onClick={add}
          className="text-xs text-violet-600 hover:text-violet-700 font-semibold inline-flex items-center gap-1"
          data-testid={`${testIdPrefix}-add`}
        >
          <Plus className="w-3.5 h-3.5" /> Add
        </button>
      </div>
      {items.length === 0 && (
        <p className="text-xs text-slate-400 italic">Nothing extracted.</p>
      )}
      <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
        {items.map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              type="text"
              placeholder={placeholder}
              value={it[labelKey] || ""}
              onChange={(e) => update(i, labelKey, e.target.value)}
              className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded-md"
              data-testid={`${testIdPrefix}-label-${i}`}
            />
            {numInput(it.count, (v) => update(i, "count", v), `${testIdPrefix}-count-${i}`)}
            <button
              onClick={() => remove(i)}
              className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500"
              data-testid={`${testIdPrefix}-remove-${i}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function VisitorIdImporter({ open, onClose, clients, API, onSaved }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState("upload");
  const [clientId, setClientId] = useState("");
  const [reportStart, setReportStart] = useState("");
  const [reportEnd, setReportEnd] = useState("");
  const [locationName, setLocationName] = useState("");
  const [file, setFile] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [extracted, setExtracted] = useState(blankState());
  const [periodLabel, setPeriodLabel] = useState("");

  const selectedClient = clients.find((c) => c.id === clientId);
  const isMulti = selectedClient?.client_type === "multi";
  const locations = selectedClient?.locations || [];

  const reset = () => {
    setStep("upload");
    setClientId("");
    setReportStart("");
    setReportEnd("");
    setLocationName("");
    setFile(null);
    setErrorMsg("");
    setExtracted(blankState());
    setPeriodLabel("");
  };

  const onSelectFile = (selected) => {
    if (!selected || selected.length === 0) return;
    setFile(selected[0]);
  };

  const runExtract = async () => {
    if (!clientId) return setErrorMsg("Pick a client first.");
    if (!reportStart || !reportEnd) return setErrorMsg("Pick a report start and end date.");
    if (!file) return setErrorMsg("Drop the Visitor ID PDF.");
    setErrorMsg("");
    setStep("extracting");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API}/import/visitor-id/extract?client_id=${clientId}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 },
      );
      const data = res.data || {};
      setExtracted({
        addresses_captured: data.addresses_captured || 0,
        verified_emails: data.verified_emails || 0,
        businesses_captured: data.businesses_captured || 0,
        age_range: Array.isArray(data.age_range) ? data.age_range : [],
        net_worth: Array.isArray(data.net_worth) ? data.net_worth : [],
        home_value: Array.isArray(data.home_value) ? data.home_value : [],
        top_industries: Array.isArray(data.top_industries) ? data.top_industries : [],
        top_pages: Array.isArray(data.top_pages) ? data.top_pages : [],
      });
      setPeriodLabel(data.report_period_label || "");
      setStep("confirm");
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || e.message || "Extraction failed");
      setStep("upload");
    }
  };

  const runSave = async () => {
    setErrorMsg("");
    setStep("saving");
    try {
      const payload = {
        client_id: clientId,
        report_start: reportStart,
        report_end: reportEnd,
        location_name: locationName || "",
        addresses_captured: Number(extracted.addresses_captured || 0),
        verified_emails: Number(extracted.verified_emails || 0),
        businesses_captured: Number(extracted.businesses_captured || 0),
        age_range: (extracted.age_range || []).filter((x) => (x.range || "").trim()),
        net_worth: (extracted.net_worth || []).filter((x) => (x.range || "").trim()),
        home_value: (extracted.home_value || []).filter((x) => (x.range || "").trim()),
        top_industries: (extracted.top_industries || []).filter((x) => (x.industry || "").trim()),
        top_pages: (extracted.top_pages || []).filter((x) => (x.url || "").trim()),
      };
      if (payload.addresses_captured === 0 && payload.verified_emails === 0) {
        setErrorMsg("Both Addresses and Verified Emails are zero — please review before saving.");
        setStep("confirm");
        return;
      }
      await axios.post(`${API}/import/visitor-id/save`, payload, { timeout: 30000 });
      setStep("done");
      if (onSaved) onSaved();
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || e.message || "Save failed");
      setStep("confirm");
    }
  };

  const updateField = (field, value) => setExtracted({ ...extracted, [field]: value });

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="visitor-id-importer-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <p className="text-lg font-bold text-slate-900">The Visitor ID Story</p>
              <p className="text-xs text-slate-500">Drop your Website Visitor ID PDF. We extract KPIs + donut charts automatically.</p>
            </div>
          </div>
          <button
            onClick={() => { reset(); onClose(); }}
            className="p-2 rounded-lg hover:bg-slate-100"
            data-testid="vid-close"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-5">
          {errorMsg && (
            <div className="mb-4 flex items-start gap-2 p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {step === "upload" && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Client</label>
                  <select
                    value={clientId}
                    onChange={(e) => { setClientId(e.target.value); setLocationName(""); }}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="vid-client-select"
                  >
                    <option value="">Select client…</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Report Start</label>
                  <input
                    type="date"
                    value={reportStart}
                    onChange={(e) => setReportStart(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="vid-report-start"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Report End</label>
                  <input
                    type="date"
                    value={reportEnd}
                    onChange={(e) => setReportEnd(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="vid-report-end"
                  />
                </div>
              </div>

              {isMulti && locations.length > 0 && (
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1">Location (optional)</label>
                  <select
                    value={locationName}
                    onChange={(e) => setLocationName(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
                    data-testid="vid-location-select"
                  >
                    <option value="">Whole brand</option>
                    {locations.map((l, i) => (
                      <option key={i} value={l.name || l}>{l.name || l}</option>
                    ))}
                  </select>
                </div>
              )}

              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onSelectFile(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center hover:border-violet-500 hover:bg-violet-50/40 transition cursor-pointer"
                data-testid="vid-drop-zone"
              >
                <UploadCloud className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                <p className="text-sm font-medium text-slate-700">
                  {file ? `${file.name} ready` : "Drop the Visitor ID PDF here, or click to browse"}
                </p>
                <p className="text-xs text-slate-500 mt-1">Vision extraction of donut charts takes ~30-60 seconds</p>
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(e) => onSelectFile(e.target.files)}
                  data-testid="vid-file-input"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { reset(); onClose(); }}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                >Cancel</button>
                <button
                  onClick={runExtract}
                  disabled={!clientId || !reportStart || !reportEnd || !file}
                  className="px-5 py-2 text-sm rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 flex items-center gap-2"
                  data-testid="vid-extract-btn"
                >
                  <Users className="w-4 h-4" /> Extract from PDF
                </button>
              </div>
            </div>
          )}

          {step === "extracting" && (
            <div className="py-16 text-center" data-testid="vid-extracting">
              <Loader2 className="w-10 h-10 mx-auto text-violet-500 animate-spin mb-3" />
              <p className="text-sm font-medium text-slate-700">Reading your Visitor ID report…</p>
              <p className="text-xs text-slate-500 mt-1">Analysing donut charts with AI Vision. ~30-60 seconds.</p>
            </div>
          )}

          {step === "confirm" && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 rounded-lg bg-violet-50 border border-violet-200 text-violet-800 text-xs">
                <Users className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold mb-0.5">Review extracted values</p>
                  <p>{periodLabel ? `Period detected: ${periodLabel}. ` : ""}Edit anything that doesn&apos;t match your PDF.</p>
                </div>
              </div>

              <div className="bg-white rounded-xl border-2 border-violet-200 p-4">
                <p className="text-sm font-bold text-slate-900 mb-3">Aggregate KPIs</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  <Field label="Addresses Captured">
                    {numInput(extracted.addresses_captured, (v) => updateField("addresses_captured", v), "vid-addresses")}
                  </Field>
                  <Field label="Verified Emails">
                    {numInput(extracted.verified_emails, (v) => updateField("verified_emails", v), "vid-emails")}
                  </Field>
                  <Field label="Businesses">
                    {numInput(extracted.businesses_captured, (v) => updateField("businesses_captured", v), "vid-businesses")}
                  </Field>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <BreakdownEditor
                  title="Age Range Breakdown (%)"
                  items={extracted.age_range}
                  onChange={(next) => updateField("age_range", next)}
                  testIdPrefix="vid-age"
                />
                <BreakdownEditor
                  title="Net Worth Breakdown (%)"
                  items={extracted.net_worth}
                  onChange={(next) => updateField("net_worth", next)}
                  testIdPrefix="vid-networth"
                />
              </div>

              <BreakdownEditor
                title="Home Value Breakdown (%)"
                items={extracted.home_value}
                onChange={(next) => updateField("home_value", next)}
                testIdPrefix="vid-homevalue"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CountList
                  title="Top Industries (from business visitors)"
                  labelKey="industry"
                  items={extracted.top_industries}
                  onChange={(next) => updateField("top_industries", next)}
                  placeholder="e.g. Offices Of Lawyers"
                  testIdPrefix="vid-industry"
                />
                <CountList
                  title="Top Pages Visited"
                  labelKey="url"
                  items={extracted.top_pages}
                  onChange={(next) => updateField("top_pages", next)}
                  placeholder="https://…"
                  testIdPrefix="vid-page"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setStep("upload")}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                >Back</button>
                <button
                  onClick={runSave}
                  className="px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 flex items-center gap-2"
                  data-testid="vid-save-btn"
                >
                  <CheckCircle2 className="w-4 h-4" /> Looks Good — Save
                </button>
              </div>
            </div>
          )}

          {step === "saving" && (
            <div className="py-16 text-center" data-testid="vid-saving">
              <Loader2 className="w-10 h-10 mx-auto text-emerald-500 animate-spin mb-3" />
              <p className="text-sm font-medium text-slate-700">Saving Visitor ID report…</p>
            </div>
          )}

          {step === "done" && (
            <div className="py-12 text-center" data-testid="vid-done">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <p className="text-base font-bold text-slate-900 mb-1">Saved!</p>
              <p className="text-sm text-slate-600 mb-5">Open the client&apos;s report to see the new Visitor ID Story section.</p>
              <div className="flex justify-center gap-2">
                <button
                  onClick={reset}
                  className="px-4 py-2 text-sm rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                  data-testid="vid-import-another"
                >Import Another</button>
                <button
                  onClick={() => { reset(); onClose(); }}
                  className="px-5 py-2 text-sm rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                  data-testid="vid-done-close"
                >Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
